import { useQueryClient } from '@tanstack/react-query';
import { applyToolEffects } from '@/lib/mediaEffects';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useCreatePost, useSetPostTags, slugify } from '@wordpuppi/shared';
import { normalizeForSave } from '@/utils/contentFormat';
import { PostEditor } from '@/components/PostEditor';
import { TagSelector } from '@/components/TagSelector';
import { PostSeoPanel, emptySeoFields, type SeoFields } from '@/components/PostSeoPanel';
import { FeatureImageCard, emptyFeatureImage, type FeatureImageFields } from '@/components/FeatureImageCard';
import { GeoPanel } from '@/components/GeoPanel';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/sites/$siteSlug/posts/new')({
  // `?title=` prefills the title (site health topic ideas, AB#1035).
  validateSearch: (search: Record<string, unknown>): { title?: string } => ({
    title: typeof search.title === 'string' ? search.title : undefined,
  }),
  component: NewPostPage,
});

function NewPostPage() {
  const { siteSlug } = Route.useParams();
  const { title: initialTitle } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createPost = useCreatePost(siteSlug);
  const setPostTags = useSetPostTags(siteSlug);

  const [title, setTitle] = useState(initialTitle ?? '');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [seo, setSeo] = useState<SeoFields>(emptySeoFields);
  const [featureImage, setFeatureImage] = useState<FeatureImageFields>(emptyFeatureImage);

  // Auto-generate slug from title
  useEffect(() => {
    if (!slugManuallyEdited && title) {
      setSlug(slugify(title));
    }
  }, [title, slugManuallyEdited]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await createPost.mutateAsync({
        title,
        slug,
        content: normalizeForSave(content),
        status,
        seo_title: seo.seo_title || undefined,
        seo_description: seo.seo_description || undefined,
        excerpt: seo.excerpt || undefined,
        featured_image: featureImage.featured_image || undefined,
        featured_image_alt: featureImage.featured_image_alt || undefined,
        featured_image_caption: featureImage.featured_image_caption || undefined,
        noindex: seo.noindex,
      });
      // Set tags if any selected
      if (selectedTagIds.length > 0) {
        await setPostTags.mutateAsync({ postId: result.data!.id, tagIds: selectedTagIds });
      }
      void applyToolEffects(queryClient, siteSlug, { name: 'wpp_create_content', args: { site_slug: siteSlug, content_type: 'posts', data: { title, content, featured_image: featureImage.featured_image } } }, { ok: true }).catch(() => {});
      toast.success('Post created');
      navigate({
        to: '/sites/$siteSlug/posts/$postId',
        params: { siteSlug, postId: result.data!.id },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create post');
    }
  };

  return (
    <div className="@container">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">New Post</h1>
      </div>

      <form onSubmit={handleSubmit} className="grid min-w-0 gap-6 @min-[52rem]:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Post title"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Content</Label>
            <PostEditor content={content} onChange={setContent} siteSlug={siteSlug} folder="posts" />
          </div>
        </div>

        <aside aria-label="Post settings" className="min-w-0 space-y-5 border-t pt-4 @min-[52rem]:border-t-0 @min-[52rem]:border-l @min-[52rem]:pt-0 @min-[52rem]:pl-6">
          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              type="text"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugManuallyEdited(true);
              }}
              placeholder="post-url-slug"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={status} onValueChange={(v: string) => setStatus(v as 'draft' | 'published')}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <FeatureImageCard
            siteSlug={siteSlug}
            value={featureImage}
            onChange={(patch) => setFeatureImage((f) => ({ ...f, ...patch }))}
          />

          <div className="space-y-2">
            <TagSelector
              siteSlug={siteSlug}
              selectedTagIds={selectedTagIds}
              onChange={setSelectedTagIds}
            />
          </div>

          <PostSeoPanel value={seo} onChange={(patch) => setSeo((s) => ({ ...s, ...patch }))} />

          {/* Author/date are assigned on save, so they stay N/A here (no nag). */}
          <GeoPanel content={content} excerpt={seo.excerpt} />

        </aside>

        <div className="flex flex-wrap gap-2 @min-[52rem]:col-span-2">
          <Button type="submit" disabled={createPost.isPending}>
            {createPost.isPending ? 'Creating...' : 'Create Post'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate({ to: '/sites/$siteSlug/posts', params: { siteSlug } })}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
